import { create } from "zustand";
import { TYPING_EXPIRY_MS } from "@/config/constants";

/**
 * Purpose: ephemeral, in-memory store of who is currently typing in each chat.
 *
 * Shape: `Map<chatId, Map<username, expiresAt>>` where `expiresAt` is an epoch
 * ms deadline. Every `typing=true` entry auto-expires after {@link TYPING_EXPIRY_MS}
 * (10s) unless refreshed — this is the receiver half of the ephemeral typing
 * contract (the sender re-emits `typing=true` every 8s as a keep-alive).
 *
 * Why expiry is non-negotiable: a crashed sender (bot OOM, laptop closed
 * mid-typing) would otherwise leave a chat showing "typing..." forever. The 10s
 * TTL guarantees the indicator self-heals. This is also why typing state must
 * NEVER be persisted to IndexedDB/localStorage — it is session-ephemeral by
 * definition and meaningless across reloads.
 *
 * Known limitation (same user on multiple devices): the broker payload carries
 * only `username`, no device/socket id. If Alice types on two devices and one
 * emits `typing=false`, this store clears Alice even though her other device is
 * still typing. We accept this for v1 — worst case is up to an ~8s flicker until
 * the still-typing device's next keep-alive re-adds her. Resolving it would
 * require a device id the broker does not send; we deliberately do not build
 * device-tracking here.
 *
 * Expired entries are pruned two ways:
 * 1. Lazily on read in {@link getTypingUsers} (so selectors never return stale
 *    usernames even between sweeps).
 * 2. By a periodic interval sweep (see {@link startTypingSweep}) so memory for
 *    inactive chats does not accumulate even if no one ever reads them.
 */

type TypingState = {
  /** chatId -> (username -> expiresAt epoch ms) */
  typingByChat: Map<string, Map<string, number>>;
};

type Actions = {
  /**
   * Record (or clear) a typing signal for a user in a chat.
   * `isTyping=true` sets/refreshes the entry with a fresh 10s deadline.
   * `isTyping=false` removes the user immediately.
   */
  setTyping: (chatId: string, username: string, isTyping: boolean) => void;
  /**
   * Active (non-expired) typing usernames for a chat, pruned lazily on read.
   * Returns a stable empty array when no one is typing.
   */
  getTypingUsers: (chatId: string) => string[];
  /** Remove all expired entries across every chat (used by the interval sweep). */
  pruneExpired: () => void;
  /** Reset the entire store (e.g. on logout). */
  resetTyping: () => void;
};

const EMPTY: string[] = [];

export const useTypingState = create<TypingState & Actions>()((set, get) => ({
  typingByChat: new Map(),

  setTyping: (chatId, username, isTyping) => {
    if (!chatId || !username) {
      return;
    }
    set((state) => {
      // Clone the outer map so subscribers re-render (Zustand uses reference
      // equality). We mutate the cloned inner maps in place — they are fresh
      // per update so this stays referentially correct.
      const next = new Map(state.typingByChat);
      const inner = new Map(next.get(chatId) ?? []);

      if (isTyping) {
        inner.set(username, Date.now() + TYPING_EXPIRY_MS);
      } else {
        inner.delete(username);
      }

      if (inner.size === 0) {
        next.delete(chatId);
      } else {
        next.set(chatId, inner);
      }

      return { typingByChat: next };
    });
  },

  getTypingUsers: (chatId) => {
    const inner = get().typingByChat.get(chatId);
    if (!inner || inner.size === 0) {
      return EMPTY;
    }

    // READ-ONLY: filter out expired entries without mutating the store. This is
    // called from render (selectors), so it must not call setState — doing so
    // would update Zustand during React render. Actual removal of expired
    // entries is handled by the interval sweep (pruneExpired) and by explicit
    // setTyping(false) calls; this just hides them from the returned list.
    const now = Date.now();
    const active: string[] = [];
    for (const [username, expiresAt] of inner) {
      if (expiresAt > now) {
        active.push(username);
      }
    }

    return active.length === 0 ? EMPTY : active;
  },

  pruneExpired: () => {
    set((state) => {
      const now = Date.now();
      let changed = false;
      const next = new Map<string, Map<string, number>>();

      for (const [chatId, inner] of state.typingByChat) {
        let hasExpired = false;
        for (const expiresAt of inner.values()) {
          if (expiresAt <= now) {
            hasExpired = true;
            break;
          }
        }

        // No expirations in this chat: keep the ORIGINAL inner-map reference so
        // per-chat subscribers (useTypingLabel selects `typingByChat.get(id)`)
        // don't re-render just because some *other* chat expired.
        if (!hasExpired) {
          next.set(chatId, inner);
          continue;
        }

        changed = true;
        const keptInner = new Map<string, number>();
        for (const [username, expiresAt] of inner) {
          if (expiresAt > now) {
            keptInner.set(username, expiresAt);
          }
        }
        if (keptInner.size > 0) {
          next.set(chatId, keptInner);
        }
      }

      return changed ? { typingByChat: next } : state;
    });
  },

  resetTyping: () => set({ typingByChat: new Map() }),
}));

/**
 * Interval sweep that removes expired typing entries even from chats no one is
 * currently looking at. Runs once per module load (the typing state is a global
 * singleton, like the socket). Guarded so it is only started once.
 */
let typingSweepInterval: ReturnType<typeof setInterval> | null = null;

export const startTypingSweep = (): void => {
  if (typingSweepInterval || typeof setInterval === "undefined") {
    return;
  }
  // Sweep at half the TTL so a stuck entry never lingers more than ~1.5x TTL.
  typingSweepInterval = setInterval(() => {
    useTypingState.getState().pruneExpired();
  }, TYPING_EXPIRY_MS / 2);
};

export const stopTypingSweep = (): void => {
  if (typingSweepInterval) {
    clearInterval(typingSweepInterval);
    typingSweepInterval = null;
  }
};

// Start the sweep eagerly in real app runtime only. Under Vitest the jsdom env
// also defines `window`, so importing this module would create a real-timer
// interval that leaks an open handle and is not controlled by tests that later
// switch to fake timers. Gate on `import.meta.env.VITEST` so tests never start
// the background sweep on import; they can call startTypingSweep explicitly.
if (typeof window !== "undefined" && !import.meta.env.VITEST) {
  startTypingSweep();
}
